import React from 'react';
import type { NewsArticleWithRelations } from '../../../types/news';
import { toast } from 'react-hot-toast';

export const useNewsHandlers = () => {
  const handleShare = (article: NewsArticleWithRelations) => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt || '',
        url: window.location.href
      }).then(() => {
        // Success
      }).catch((error) => {
        console.error('Error sharing:', error);
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleVideoToggle = (videoRef: React.RefObject<HTMLVideoElement>, videoMuted: boolean, setVideoMuted: (muted: boolean) => void) => {
    if (videoRef.current) {
      setVideoMuted(!videoMuted);
      videoRef.current.muted = !videoMuted;
      
      // Auto-play when unmuting
      if (!videoMuted) {
        videoRef.current.play().catch(err => console.error('Error playing video:', err));
      }
    }
  };
  
  const scrollToContent = () => {
    const contentSection = document.getElementById('news-content');
    if (contentSection) {
      contentSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent, mouseX: any, mouseY: any) => {
    const { clientX, clientY } = e;
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    
    // Calculate distance from center with easing for smoother effect
    const distanceX = (clientX - centerX) / centerX;
    const distanceY = (clientY - centerY) / centerY;
    
    mouseX.set(distanceX * 20); // Amplify effect
    mouseY.set(distanceY * 20);
  };
  
  const handleBookmark = (article: NewsArticleWithRelations) => {
    // Get existing bookmarks from localStorage
    const bookmarks = JSON.parse(localStorage.getItem('news_bookmarks') || '[]');
    
    // Check if article is already bookmarked
    const isBookmarked = bookmarks.some((bookmark: any) => bookmark.id === article.id);
    
    if (isBookmarked) {
      // Remove from bookmarks
      const updatedBookmarks = bookmarks.filter((bookmark: any) => bookmark.id !== article.id);
      localStorage.setItem('news_bookmarks', JSON.stringify(updatedBookmarks));
      toast.success('Removed from bookmarks');
    } else {
      // Add to bookmarks
      const simplifiedArticle = {
        id: article.id,
        title: article.title,
        slug: article.slug,
        cover_image: article.cover_image,
        excerpt: article.excerpt,
        published_at: article.published_at,
        category: article.category ? { name: article.category.name, slug: article.category.slug } : null
      };
      
      bookmarks.push(simplifiedArticle);
      localStorage.setItem('news_bookmarks', JSON.stringify(bookmarks));
      toast.success('Added to bookmarks');
    }
    
    return !isBookmarked; // Return new bookmark state
  };
  
  const isArticleBookmarked = (articleId: string) => {
    const bookmarks = JSON.parse(localStorage.getItem('news_bookmarks') || '[]');
    return bookmarks.some((bookmark: any) => bookmark.id === articleId);
  };

  return {
    handleShare,
    handleVideoToggle,
    scrollToContent,
    handleMouseMove,
    handleBookmark,
    isArticleBookmarked
  };
};